import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinInvoicesListWithFiltersQuery } from 'store/api/invoices.ts';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { ClassifierData } from 'types/classifiers';
import { ExpenseStatusType } from 'types/expenses';
import {
    enrichDynamicFiltersWithPassiveFilter,
    filterMainFilters,
    PaginatedFieldPassiveFilter,
} from 'utils/filterUtil.ts';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierTextDescription, t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';

export const EXPENSES_FILTER_STATUS_TYPES = [
    ExpenseStatusType.FINISHED,
    ExpenseStatusType.DRAFT_INVOICE,
    ExpenseStatusType.INVOICED,
    ExpenseStatusType.PAID,
];

/** What each status means in invoicing terms; the FINISHED status is labelled "Uninvoiced" on the list. */
const EXPENSE_STATUS_TOOLTIPS: Partial<Record<string, string>> = {
    [ExpenseStatusType.FINISHED]: 'expenses.tooltips.status-uninvoiced',
    [ExpenseStatusType.DRAFT_INVOICE]: 'expenses.tooltips.status-draft-invoice',
    [ExpenseStatusType.INVOICED]: 'expenses.tooltips.status-invoiced',
    [ExpenseStatusType.PAID]: 'expenses.tooltips.status-paid',
};

export type ExpensesFilterId = 'expenseClientFilter' | 'matterIdInFilter';
export type ExpensesFilterGroup = never;

type BuildArgs = {
    classifiers: Record<string, ClassifierData>;
    expenseStatusTypeOptions: ReturnType<typeof mapToFilterOptions>;
    passiveFilter: string;
    disabledGroups?: ExpensesFilterGroup[];
    disabledFilterIds?: ExpensesFilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<ExpensesFilterId>[];
};

/**
 * Shared filter config for the global Expenses page — used by both the flat List view
 * (`ExpensesTable`) and the Grouped view (`ExpensesGroupedTable`). Kept in one place so
 * the two views can never drift out of sync as new filters are added. Caller resolves
 * `passiveFilter` (e.g. matter-scoped filter for the matter context).
 */
export const buildExpensesFilterConfig = ({
    classifiers,
    expenseStatusTypeOptions,
    passiveFilter,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
}: BuildArgs): FilterConfig => ({
    quickFilters: [
        {
            selector: 'id',
            comperator: '=ismyexpense=',
            filters: [{ label: t('expenses.my-expenses'), argument: 'true' }],
        },
        {
            selector: 'timecard.id',
            comperator: '=gt=',
            filters: [{ label: t('expenses.connected-to-timecard'), argument: '0' }],
        },
    ],
    filters: enrichDynamicFiltersWithPassiveFilter(
        filterMainFilters(
            [
                {
                    group: 'expenses.filter.details',
                    type: 'daterange',
                    selector: 'expenseDate',
                    shouldDisplayQuickPick: true,
                    label: t('date-from'),
                    labelTo: t('date-until'),
                },
                {
                    id: 'expenseClientFilter',
                    group: 'expenses.filter.details',
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    selector: 'id',
                    label: t('expenses.filter.client'),
                    comperator: '=isexpenseclient=',
                    preserveValueWhenHidden: true,
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    id: 'matterIdInFilter',
                    group: 'expenses.filter.details',
                    type: 'dynamicmultiselect',
                    selector: 'matter.id',
                    label: t('expenses.filter.case'),
                    preserveValueWhenHidden: true,
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersListQuery,
                        searchField: ['fullName', 'clientParty.name'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                        sort: ['clientParty.name,asc', 'name,asc'],
                        groupByField: 'clientParty.name',
                    },
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=ismyexpense=',
                    group: 'expenses.filter.details',
                    filters: [{ label: t('expenses.my-expenses'), argument: 'true' }],
                },
                {
                    group: 'expenses.filter.details',
                    type: 'dynamicmultiselect',
                    selector: 'employee.id',
                    label: t('expenses.filter.expense-done-by'),
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    group: 'expenses.filter.details',
                    type: 'dynamicmultiselect',
                    selector: 'id',
                    label: t('expenses.filter.colleagues-office'),
                    comperator: '=iscolleaguesoffice=',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    group: 'expenses.filter.details',
                    type: 'multiselect',
                    selector: 'expenseTypeCode',
                    label: t('expenses.filter.expense-type'),
                    options: getFilterOptionsFromClassifier(classifiers?.['EXPENSE_TYPE']).map((option) => {
                        const description = getClassifierTextDescription(classifiers?.['EXPENSE_TYPE'], option.value);
                        return description && description.trim() && description !== option.value
                            ? { ...option, label: `${option.label} (${description})` }
                            : option;
                    }),
                },
                {
                    group: 'expenses.filter.details',
                    type: 'multiselect',
                    selector: 'currencyTypeCode',
                    label: t('expenses.filter.expense-currency'),
                    options: getValidOptionsFromClassifier<string, string>(classifiers?.['CURRENCY']).map(
                        ({ label, value }) => ({ label, value })
                    ),
                },
                {
                    group: 'expenses.filter.details',
                    type: 'multiselect',
                    selector: 'originalCurrencyTypeCode',
                    label: t('expenses.filter.expense-original-currency'),
                    options: getValidOptionsFromClassifier<string, string>(classifiers?.['CURRENCY']).map(
                        ({ label, value }) => ({ label, value })
                    ),
                },
                {
                    group: 'expenses.filter.details',
                    selector: 'id',
                    comperator: '==',
                    label: t('expenses.filter.expense-id'),
                    type: 'text',
                },
                {
                    group: 'expenses.filter.details',
                    selector: 'externalId',
                    label: t('expenses.filter.expense-external-id'),
                    type: 'text',
                },
                {
                    group: 'expenses.filter.details',
                    type: 'dynamicmultiselect',
                    selector: 'invoiceExpenses.invoice.id',
                    label: t('expenses.filter.invoice-number'),
                    paginated: {
                        fetch: useLazyGetMinInvoicesListWithFiltersQuery,
                        searchField: 'invoiceNumber',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'invoiceNumber',
                    },
                },
                {
                    group: 'expenses.filter.details',
                    type: 'multiselect',
                    selector: 'invoiceStatusTypeCode',
                    label: t('expenses.filter.connected-invoice-status'),
                    options: getFilterOptionsFromClassifier(classifiers?.['INVOICE_STATUS_TYPE']),
                },
                {
                    group: 'expenses.filter.details',
                    type: 'daterange',
                    label: t('expenses.filter.connected-invoice-paid'),
                    shouldDisplayQuickPick: true,
                    selector: 'invoiceExpenses.invoice.paidOnDate',
                },
                {
                    group: 'expenses.filter.details',
                    type: 'checkboxgroup',
                    selector: 'isNonBillable',
                    label: t('expenses.filter.billing-type'),
                    filters: [
                        { label: 'Billable', argument: 'false' },
                        { label: 'Non-billable', argument: 'true' },
                    ],
                },
                {
                    group: 'expenses.filter.details',
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=isuninvoicedforclosing=',
                    filters: [
                        {
                            label: t('expenses.filter.blocks-case-closing'),
                            argument: 'true',
                            tooltip: 'expenses.tooltips.blocks-case-closing',
                        },
                    ],
                },
                {
                    group: 'expenses.filter.details',
                    type: 'checkboxgroup',
                    selector: 'expenseStatusTypeCode',
                    label: t('expenses.filter.expense-status'),
                    multiple: true,
                    filters: expenseStatusTypeOptions.map((option) => ({
                        ...option,
                        tooltip: EXPENSE_STATUS_TOOLTIPS[option.value],
                    })),
                },
            ],
            disabledGroups,
            disabledFilterIds
        ),
        paginatedFieldPassiveFilters
    ),
    searchBarSelectors: [
        { selector: 'description' },
        { selector: 'employee.fullName' },
        { selector: 'matter.name' },
        { selector: 'matter.caseNumber' },
        { selector: 'matter.clientParty.name' },
    ],
    passiveFilter,
});
